import { useCallback, useEffect, useState } from "react";
import { getModels } from "../services/models";
import { buildModelOptions } from "../utils/modelRegistry";

// Model lists are shared by the generator dialog and every LLM node on the canvas, so the
// fetch is memoised per client rather than repeated per component.
const pending = new Map();

const fetchModels = (clientId, { force = false } = {}) => {
  const key = String(clientId ?? "all");

  if (!force && pending.has(key)) {
    return pending.get(key);
  }

  const promise = getModels(clientId ? { clientId } : {})
    .then((result) => {
      if (!result.success) {
        throw new Error(result.message || "Could not load the model registry.");
      }
      return result.models ?? [];
    })
    .catch((error) => {
      pending.delete(key);
      throw error;
    });

  pending.set(key, promise);
  return promise;
};

/**
 * Registered models available for selection.
 *
 * @param {object} [options]
 * @param {string|null} [options.clientId] Scope the list to a client (plus the globals).
 * @returns {{models: object[], options: {value: string, label: string}[], loading: boolean,
 *   error: string|null, reload: () => void}}
 */
export const useModelOptions = ({ clientId = null } = {}) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    fetchModels(clientId, { force: nonce > 0 })
      .then((list) => {
        if (!cancelled) {
          setModels(list);
          setLoading(false);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setModels([]);
          setError(loadError?.message || "Could not load the model registry.");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { models, options: buildModelOptions(models), loading, error, reload };
};

export default useModelOptions;
